export function PrivacyPage() {
  return (
    <div className="max-w-3xl mx-auto px-6 py-16 space-y-10">
      <div className="space-y-3">
        <h1 className="text-3xl font-bold tracking-tight">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground">Last updated: June 2026</p>
        <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          This policy is a working draft pending review by legal counsel. It does not constitute
          legal advice and will be revised before public launch.
        </div>
      </div>

      <div className="space-y-8 text-sm leading-relaxed text-muted-foreground">

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">1. Who we are</h2>
          <p>
            Direct Campaign (&quot;Direct Campaign,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) operates the field canvassing
            platform available at directcampaign.org. We provide software tools for political
            campaigns and advocacy organizations to manage voter outreach, walkbooks, and volunteer
            canvassing programs.
          </p>
          <p>
            Our principal place of business is in the United States. For privacy-related questions,
            contact us at{" "}
            <a href="mailto:privacy@directcampaign.org" className="text-foreground underline">
              privacy@directcampaign.org
            </a>
            .
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">2. Information we collect</h2>
          <p>
            We collect information in the following categories:
          </p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong className="text-foreground">Account information.</strong> When a campaign
              administrator creates an account or invites a user, we collect name, email address,
              and role within the organization.
            </li>
            <li>
              <strong className="text-foreground">Voter data uploaded by campaigns.</strong> Campaign
              administrators upload voter files (CSV format) that may contain names, addresses,
              phone numbers, party affiliation, and other publicly available voter registration
              information. This data is uploaded and managed by the campaign — Direct Campaign
              processes it only to provide the services described in your agreement.
            </li>
            <li>
              <strong className="text-foreground">Canvassing interaction data.</strong> Canvassers
              log door-knock results, survey responses, and notes through the platform. This
              information is generated by the campaign&apos;s field program and remains the campaign&apos;s
              data.
            </li>
            <li>
              <strong className="text-foreground">Usage data.</strong> We collect standard server
              logs, including IP addresses, browser type, pages visited, and timestamps. This data
              is used to operate and improve the platform.
            </li>
            <li>
              <strong className="text-foreground">Contact form submissions.</strong> If you contact
              us via a form on this website, we collect the information you provide (name, email,
              and message content) in order to respond to your inquiry.
            </li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">3. How we use your information</h2>
          <p>We use collected information to:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>Provide, operate, and maintain the Direct Campaign platform</li>
            <li>Authenticate users and enforce role-based access controls</li>
            <li>Process voter data on behalf of campaigns as a data processor</li>
            <li>Respond to support requests and inquiries</li>
            <li>Send transactional communications (account invitations, password resets)</li>
            <li>Monitor for security threats and abuse</li>
            <li>Comply with legal obligations</li>
          </ul>
          <p>
            We do not sell your personal information or voter data to third parties. We do not use
            voter data uploaded to the platform for advertising or any purpose other than providing
            the contracted services.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">4. Voter data — campaign responsibility</h2>
          <p>
            Direct Campaign acts as a <strong className="text-foreground">data processor</strong>{" "}
            for voter data uploaded by campaigns. The campaign is the data controller and is
            responsible for ensuring that voter data is obtained and used in compliance with
            applicable federal and state election laws, voter privacy regulations, and any
            applicable data protection requirements.
          </p>
          <p>
            Voter data is logically isolated per organization — no campaign can access another
            campaign&apos;s data. All voter data is stored in encrypted form in the United States.
          </p>
          <p>
            Campaigns may export or request deletion of their voter data at any time by contacting{" "}
            <a href="mailto:privacy@directcampaign.org" className="text-foreground underline">
              privacy@directcampaign.org
            </a>
            .
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">5. Data sharing and disclosure</h2>
          <p>We do not sell or rent personal information. We may share information with:</p>
          <ul className="list-disc pl-5 space-y-2">
            <li>
              <strong className="text-foreground">Infrastructure providers.</strong> We use Amazon
              Web Services (AWS) to host the platform and store data. AWS operates under its own
              privacy and security frameworks.
            </li>
            <li>
              <strong className="text-foreground">Service providers.</strong> We may engage vendors
              for email delivery, analytics, or other operational functions. These vendors are
              contractually prohibited from using your data for any purpose other than providing
              services to us.
            </li>
            <li>
              <strong className="text-foreground">Legal requirements.</strong> We may disclose
              information if required by law, subpoena, court order, or to protect the rights,
              property, or safety of Direct Campaign, our users, or the public.
            </li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">6. Data retention</h2>
          <p>
            We retain account information for as long as your organization&apos;s account is active. If
            you terminate your subscription, we will delete or anonymize your data within 90 days,
            unless longer retention is required by law.
          </p>
          <p>
            Voter data is retained for the duration of the campaign&apos;s active subscription. Upon
            termination, voter data is deleted within 90 days unless a longer retention period is
            requested in writing for legitimate campaign archival purposes.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">7. Security</h2>
          <p>
            We use industry-standard technical and organizational measures to protect your
            information, including encryption in transit (TLS) and at rest, access controls, and
            multi-factor authentication for administrative systems. No system is completely secure —
            if you believe your account has been compromised, contact us immediately at{" "}
            <a href="mailto:privacy@directcampaign.org" className="text-foreground underline">
              privacy@directcampaign.org
            </a>
            .
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">8. Cookies and tracking</h2>
          <p>
            We use cookies strictly necessary for platform operation (session authentication). We do
            not use third-party advertising cookies or cross-site tracking. Usage analytics, if any,
            are collected in aggregate and not tied to individual users.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">9. Your rights</h2>
          <p>
            Depending on your jurisdiction, you may have rights to access, correct, or request
            deletion of personal information we hold about you. To exercise these rights, contact us
            at{" "}
            <a href="mailto:privacy@directcampaign.org" className="text-foreground underline">
              privacy@directcampaign.org
            </a>
            . We will respond within 30 days.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">10. Changes to this policy</h2>
          <p>
            We may update this policy from time to time. Material changes will be communicated to
            account holders via email at least 14 days before taking effect. Continued use of the
            platform after that date constitutes acceptance of the updated policy.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-foreground">11. Contact</h2>
          <p>
            For privacy questions, data requests, or to report a concern, contact us at{" "}
            <a href="mailto:privacy@directcampaign.org" className="text-foreground underline">
              privacy@directcampaign.org
            </a>
            .
          </p>
        </section>

      </div>
    </div>
  );
}
